import { Component, Inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { NgArrayPipesModule } from 'ngx-pipes'

import { AssetMini, BootstrapData, ClickGallery, ClickGalleryLink, CloudConnection } from 'elements-sdk'
import { APIService } from 'api'
import { BOOTSTRAP_DATA } from 'api/providers'
import { MessageBoxService, SocketService } from 'common/util'
import { NotifyService } from 'common/util/services/notify.service'
import { ModalComponent, ModalService } from 'common/util.modals'
import { TranslateDirective } from 'common/locale/directives/translate.directive'
import { SmartProgressComponent } from 'common/util/components/smartProgress.component'
import { SpinnerComponent } from 'common/util/components/spinner.component'
import { FilterboxComponent } from 'common/util/components/filterbox.component'
import { AutoselectDirective } from 'common/util/directives/autoselect.directive'
import { SRLabelDirective } from 'common/util/directives/label.directive'
import { ClickBlockingDirective } from 'common/util/directives/clickBlocking.directive'
import { NaturalOrderByPipe } from 'common/util/pipes/naturalOrderBy.pipe'
import { TranslatePipe } from 'common/locale'
import { IdentityService } from 'main/services/identity.service'
import { TaskState } from 'tasks/constants'
import { AssetThumbnailComponent } from 'media.thumbnails/components/assetThumbnail.component'

import { CreateClickLinkModalComponent } from './createClickLinkModal.component'
import { CreateClickGalleryModalComponent } from './createClickGalleryModal.component'

interface Params {
  readonly assets: ReadonlyArray<AssetMini>
  readonly connection: CloudConnection
}

@Component({
  selector: 'click-sharing-modal',
  templateUrl: './clickSharingModal.component.html',
  imports: [
    TranslateDirective,
    AssetThumbnailComponent,
    SmartProgressComponent,
    SpinnerComponent,
    FilterboxComponent,
    FormsModule,
    AutoselectDirective,
    SRLabelDirective,
    ClickBlockingDirective,
    NaturalOrderByPipe,
    NgArrayPipesModule,
    TranslatePipe,
  ],
})
export class ClickSharingModalComponent extends ModalComponent<Params, null> {
  progress: { readonly asset: AssetMini, done: boolean }[] = []
  links: ClickGalleryLink[] = []
  galleries: ClickGallery[] = []
  gallery?: ClickGallery
  taskId?: string
  uploadedCount = 0
  galleryIsNew = false
  galleriesLoading = false
  currentAsset?: AssetMini
  connections = this.bootstrapData.cloudConnections
  notifyOnCompletion = true
  step = ''
  galleriesFilter: any

  constructor (
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
    private api: APIService,
    private identity: IdentityService,
    private messageBox: MessageBoxService,
    private readonly modal: ModalService,
    private translate: TranslateService,
    private notify: NotifyService,
    socket: SocketService,
  ) {
    super()

    socket.subject('cloud:upload-complete')
      .pipe(takeUntilDestroyed())
      .subscribe(data => {
        this.progress.filter(x => x.asset.id === data.asset_id).forEach(progressItem => {
          progressItem.done = true
        })
        this.uploadedCount++
        if (!this.progress.every(x => x.done)) {
          this.currentAsset = this.progress.find(x => !x.done)?.asset
        }
      })

    socket.subject('cloud:upload-failed')
      .pipe(takeUntilDestroyed())
      .subscribe(data => {
        this.onError(data.reason)
      })

    socket.subject('tasks:done')
      .pipe(takeUntilDestroyed())
      .subscribe(data => {
        if (data.id === this.taskId) {
          if (data.state === TaskState.FINISHED || data.state === TaskState.WARNINGS) {
            this.finishUpload()
            this.currentAsset = undefined
          }
        }
      })
  }

  async ngOnInit () {
    this.progress = this.params.assets.map(x => ({
      asset: x,
      done: false,
    }))
    this.currentAsset = this.params.assets[0]
    this.startUpload()
    this.galleriesLoading = true
    try {
      this.galleries = await this.api.click.getAllClickGalleries({
        connectionId: this.params.connection.id.toString(),
      })
    } catch (error) {
      this.notify.errorFromResponse(this.translate.instant(`Could not load galleries`), error)
    }

    this.galleriesLoading = false
  }

  onError (reason: string) {
    this.notify.error(this.translate.instant(`Sharing failed`), reason)
    this.abort()
  }

  close () {
    this.dismiss()
  }

  async abort () {
    try {
      await this.messageBox.show({
        text: this.translate.instant(`Abort the upload?`),
        positive: this.translate.instant(`Abort`),
        negative: this.translate.instant(`Continue`),
        danger: true,
      })
    } catch {
      return
    }
    if (this.taskId) {
      await this.api.click.abortClickUpload({ uploadId: this.taskId })
    }
    this.close()
  }

  selectGallery (gallery) {
    this.galleryIsNew = false
    if (gallery) {
      this.gallery = gallery
      if (this.step === 'done') {
        this.save()
      }
    } else {
      this.createGallery()
    }
  }

  async createGallery () {
    let name = `${this.params.assets.length} assets on ${new Date().toDateString()}`
    if (this.params.assets.length === 1) {
      name = this.params.assets[0]!.displayName
      if (name.includes('.')) {
        name = name.substring(0, name.lastIndexOf('.'))
      }
    }

    this.gallery = await this.modal.open(CreateClickGalleryModalComponent, { name, connection: this.params.connection }) ?? undefined
    if (!this.gallery) {
      return
    }
    this.galleryIsNew = true
    if (this.step === 'done') {
      this.save()
    }
  }

  async save () {
    await this.api.click.addAssetsToClickGallery({
      connectionId: this.params.connection.id.toString(),
      id: this.gallery!.id.toString(),
      addAssetsToClickGallery: {
        assets: this.progress.map(x => x.asset.syncId),
      },
    })

    this.step = 'saved'
    this.sendEmails()
  }

  async sendEmails () {
    for (const link of this.links.filter(x => x.elementsUser?.email)) {
      await this.api.click.sendClickGalleryLinkEmail({
        connectionId: this.params.connection.id.toString(),
        linkId: link.id!.toString(),
      })
    }
  }

  readonly background = async () => {
    await this.api.click.continueClickUploadInBackground({
      uploadId: this.taskId!,
      clickBackgroundUploadEndpointRequest: {
        gallery: this.gallery!.id,
        linksToSend: this.links.filter(x => x.elementsUser?.email).map(x => x.id!),
        notifyOnCompletion: this.notifyOnCompletion ? !!this.identity.user?.id : false,
      },
    })
    this.notify.info(this.translate.instant(`Continuing in background`))
    this.complete(null)
  }

  async startUpload () {
    try {
      this.step = 'uploading'
      this.uploadedCount = 0
      this.taskId = (await this.api.click.startClickUpload({
        clickStartUploadEndpointRequest: {
          connection: this.params.connection.id,
          assets: this.params.assets.map(x => x.id),
        },
      })).id
    } catch (error) {
      this.onError(error.message)
    }
  }

  finishUpload () {
    if (this.gallery) {
      this.save()
    } else {
      this.step = 'done'
    }
  }

  async createLink () {
    const result = await this.modal.open(CreateClickLinkModalComponent, { gallery: this.gallery, connection: this.params.connection })
    if (!result) {
      return
    }
    const { clickGalleryLink: link, sendEmail } = result
    this.links.push(link)
    if (this.step === 'saved' && link.elementsUser?.email && sendEmail) {
      this.api.click.sendClickGalleryLinkEmail({
        connectionId: this.params.connection.id.toString(),
        linkId: link.id!.toString(),
      })
    }
  }

  async deleteLink (link: ClickGalleryLink) {
    await this.messageBox.deletePrompt(this.translate.instant(`link`), ``)
    await this.api.click.deleteClickGalleryLink({
      connectionId: this.params.connection.id.toString(),
      id: link.id as any,
    })
    this.links = this.links.filter(x => x !== link)
  }
}
